import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import NavigationBar from './navigation-bar';
import MobileBottomNav from './MobileBottomNav';
import CoverageSidebar from './CoverageSidebar';
import AddressSearchResults from './AddressSearchResults';
import CoverageResults from './CoverageResults';
import PackageComparisonModal from './PackageComparisonModal';
import { MegaMenuManager } from './mega-menus';
import { SignupModal, SignupSummary, SignupSuccessModal } from './signup';
import { submitSignupForm } from './signup/api';
import { AlertTriangle } from 'lucide-react';
import QuickNavBar from '../Shared/QuickNavBar';
import { ComparisonModalManager } from '../ComparisonModals';
import useComparisonStore from '../ComparisonModals/store/comparisonStore';

/**
 * WonderNavigation - Main navigation component
 *
 * This component combines the NavigationBar, MobileBottomNav, and CoverageSidebar components
 * to create a complete navigation system with address search and coverage check
 * functionality. The design uses a modern, mobile-first approach with a dark theme
 * featuring gradients, subtle animations, and a clean, accessible interface.
 */
const WonderNavigation = forwardRef(({
  openComparisonModal: externalOpenComparisonModal = null,
  openRecommendationModal: externalOpenRecommendationModal = null,
  openRouterFeaturesModal = null,
  openISPFeaturesModal = null
}, ref) => {
  const [isCoverageSidebarOpen, setIsCoverageSidebarOpen] = useState(false);
  const [initialCoordinates, setInitialCoordinates] = useState(null);
  const [initialAddress, setInitialAddress] = useState(null);
  const [mobileMenuAction, setMobileMenuAction] = useState(null);
  
  // Scroll-related state
  const [showQuickLinks, setShowQuickLinks] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Lifted address state from CoverageSidebar
  const [selectedAddress, setSelectedAddress] = useState(null);
  // Package comparison modal state
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [allOperators, setAllOperators] = useState([]);
  // Recommendation modal state
  const [isRecommendationModalOpen, setIsRecommendationModalOpen] = useState(false);
  
  // Get actions from the Zustand store
  const {
    openSelectionModal,
    openRecommendationModal: openRecommendationModalStore,
    openRecommendationModalOnly,
    closeAllModals
  } = useComparisonStore();
  
  // Function to open the recommendation modal
  const openRecommendationModal = () => {
    // First close the package comparison modal
    setIsComparisonModalOpen(false);
    
    // Then close the coverage sidebar
    setIsCoverageSidebarOpen(false);
    
    // Use the Zustand store to open the recommendation modal
    setTimeout(() => {
      // First close all modals to ensure a clean state
      closeAllModals();
      
      // Then open only the recommendation modal using the store function directly
      const store = useComparisonStore.getState();
      store.openRecommendationModalOnly();
    }, 200);
  };
  
  
  // Signup modal state
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [signupPackage, setSignupPackage] = useState(null);
  const [signupProviderName, setSignupProviderName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [signupFormData, setSignupFormData] = useState({
    // Default form values
    title: '',
    firstName: '',
    surname: '',
    email: '',
    cellphone: '',
    idNumber: '',
    promoCode: '',
    referralCode: '',
    existingCustomer: 'no',
    routerOption: 'freeRouter', // Default to free router
    installationStatus: '',
    installationDate: '',
    tooltipAcknowledged: false, // For tracking installation notice acknowledgment
    streetName: '',
    suburb: '',
    complex: '',
    unitNumber: '',
    city: '',
    postalCode: '',
    propertyType: '',
    propertyOwnership: '',
    landlordName: '',
    landlordContact: '',
    landlordEmail: '',
    accountNumber: '',
    accountHolderName: '',
    bank: '',
    branchName: '',
    branchCode: '',
    accountType: '',
    authoriseDebitOrder: false,
    agreeToTerms: false,
    signature: null
  });
  
  // Filter state (moved from PackageComparisonModal to here)
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [sortBy, setSortBy] = useState('price');
  const [sortDirection, setSortDirection] = useState('asc');
  const [speedFilter, setSpeedFilter] = useState([0, 1000]);
  const [priceFilter, setPriceFilter] = useState([0, 2000]);
  
  // Geolocation warning state
  const [isUsingGeolocation, setIsUsingGeolocation] = useState(false);
  
  // Track if components are open for side-by-side layout
  const isSideBySideLayout = (isCoverageSidebarOpen && isComparisonModalOpen) ||
                             (isCoverageSidebarOpen && isSignupOpen) ||
                             (isSignupOpen);
  
  // Handle scroll events to show/hide navigation components
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Don't change navigation visibility when sidebar is open
      if (isCoverageSidebarOpen || isComparisonModalOpen || isSignupOpen) {
        return;
      }
      
      // Check if we're on the Home or Home2 page
      const isHomePage = window.location.pathname === '/' || window.location.pathname === '/home-2';
      
      // Get the height of the main navigation bar (approximately 64px)
      const navHeight = 64;
      
      if (isHomePage) {
        // On Home page, handle the transition between main nav and quick nav
        const dividerNav = document.querySelector('.home-divider-nav');
        if (dividerNav) {
          const dividerRect = dividerNav.getBoundingClientRect();
          
          // Trigger the transition when the divider reaches the bottom of the main nav
          if (dividerRect.top <= navHeight && !showQuickLinks && !isTransitioning) {
            // First hide the main nav
            setIsTransitioning(true);
            setShowQuickLinks(false);
            
            // Then after the main nav is hidden, show the quick nav
            setTimeout(() => {
              setShowQuickLinks(true);
              setIsTransitioning(false);
            }, 400); // This should match the animation duration of the main nav
          }
          // When scrolling up, hide quick nav immediately
          else if (dividerRect.top > navHeight && showQuickLinks) {
            setShowQuickLinks(false);
          }
        } else {
          // Fallback to default behavior if divider not found
          if (currentScrollY > 100 && !showQuickLinks && !isTransitioning) {
            setIsTransitioning(true);
            setShowQuickLinks(false);
            
            setTimeout(() => {
              setShowQuickLinks(true);
              setIsTransitioning(false);
            }, 400);
          } else if (currentScrollY <= 100 && showQuickLinks) {
            setShowQuickLinks(false);
          }
        }
      } else {
        // Default behavior for other pages
        if (currentScrollY > 100 && currentScrollY > lastScrollY && !showQuickLinks && !isTransitioning) {
          setIsTransitioning(true);
          setShowQuickLinks(false);
          
          setTimeout(() => {
            setShowQuickLinks(true);
            setIsTransitioning(false);
          }, 400);
        } else if ((currentScrollY < 50 || currentScrollY < lastScrollY) && showQuickLinks) {
          setShowQuickLinks(false);
        }
      }
      
      setLastScrollY(currentScrollY);
    };
    
    // Add throttling to prevent performance issues
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [lastScrollY, isCoverageSidebarOpen, isComparisonModalOpen, isSignupOpen]);
  
  const handleMobileMenuClick = (action) => {
    setMobileMenuAction(action);
    
    if (action === 'coverage') {
      setIsCoverageSidebarOpen(true);
    } else if (action === 'menu') {
      // This will trigger the NavigationBar to open the mobile menu
      // The NavigationBar component will handle toggling the mobile menu
      setMobileMenuAction('menu');
    }
    // Other mobile menu actions can be handled here
  };
  
  // Function to open the coverage sidebar, optionally with coordinates and address
  const openCoverageSidebar = (coordinates = null, address = null) => {
    if (coordinates && address) {
      setInitialCoordinates(coordinates);
      setInitialAddress(address);
      
      // Also update the main selectedAddress state with the full address details
      // This ensures the coordinates are available when proceeding to signup
      const fullAddressDetails = {
        ...address,
        placeDetails: {
          ...address.placeDetails,
          geometry: {
            ...((address.placeDetails && address.placeDetails.geometry) || {}),
            location: coordinates // Ensure coordinates are included
          }
        }
      };
      
      // Update the main selectedAddress state
      setSelectedAddress(fullAddressDetails);
    } else {
      setInitialCoordinates(null);
      setInitialAddress(null);
    }
    
    // Don't change the navigation visibility state when opening the sidebar
    setIsCoverageSidebarOpen(true);
  };
  
  // Expose the openCoverageSidebar function through the ref
  useImperativeHandle(ref, () => ({
    openCoverageSidebar
  }));

  // Function to close the coverage sidebar
  const closeCoverageSidebar = () => {
    setIsCoverageSidebarOpen(false);
    // Reset initial values after sidebar is closed
    setTimeout(() => {
      setInitialCoordinates(null);
      setInitialAddress(null);
    }, 500); // Match the animation duration
    
    // Restore scroll-based navigation behavior if other modals are closed
    if (!isComparisonModalOpen && !isSignupOpen) {
      // Check current scroll position to determine which nav to show
      const currentScroll = window.scrollY;
      setShowQuickLinks(currentScroll > 100);
    }
  };
  
  // Function to open the package comparison modal
  const openPackageComparisonModal = (operator, operators = []) => {
    // If called without parameters (from mega menu), use Zustand store
    if (!operator && operators.length === 0) {
      openSelectionModal({ selectAllOperators: true });
    }
    // If called with parameters (from coverage sidebar), use internal modal
    else {
      setSelectedOperator(operator);
      setAllOperators(operators);
      setIsComparisonModalOpen(true);
    }
  };
  
  // Function to close the package comparison modal
  const closePackageComparisonModal = () => {
    setIsComparisonModalOpen(false);
    
    // Restore scroll-based navigation behavior if sidebar is also closed
    if (!isCoverageSidebarOpen && !isSignupOpen) {
      // Check current scroll position to determine which nav to show
      const currentScroll = window.scrollY;
      setShowQuickLinks(currentScroll > 100);
    }
  };
  
  // Function to open the signup modal
  const openSignup = (packageData, providerName) => { // Remove coordinates parameter
    setSignupPackage(packageData);
    setSignupProviderName(providerName);
    // setSignupCoordinates(coordinates); // Remove this state update
    setIsSignupOpen(true);
    setIsComparisonModalOpen(false); // Close the comparison modal
    setIsCoverageSidebarOpen(false); // Close the coverage sidebar
  };
  
  // Function to close the signup modal
  const closeSignup = () => {
    setIsSignupOpen(false);
    
    // Restore scroll-based navigation behavior if other modals are closed
    if (!isCoverageSidebarOpen && !isComparisonModalOpen) {
      // Check current scroll position to determine which nav to show
      const currentScroll = window.scrollY;
      setShowQuickLinks(currentScroll > 100);
    }
  };
  
  // Function to update signup form data
  const updateSignupFormData = (newData) => {
    setSignupFormData(newData);
  };

  // Function to handle signup submission
  const handleSignupSubmit = async (formData) => {
    console.log('Signup form submitted:', formData);
    
    try {
      setIsSubmitting(true);
      
      // Call the API to submit the form
      const response = await submitSignupForm(formData);
      
      if (response.success) {
        console.log('Signup successful:', response);
        
        // Extract reference number from the response
        const ref = response.data?.referenceNumber ||
                   response.data?.data?.referenceNumber ||
                   'WN-0000-NEW-000000000';
        
        // Set the reference number for the success modal
        setReferenceNumber(ref);
        
        // Update the signup form data with the submit result
        // This will display the success message within the SignupModal
        setSignupFormData(prevData => ({
          ...prevData,
          submitResult: {
            success: true,
            message: 'Your application has been submitted successfully!',
            data: {
              referenceNumber: ref
            }
          }
        }));
        
        // Don't close the signup modal immediately
        // Let the user see the success message and reference number
        
        // After a delay, we can optionally close the signup modal and show the success modal
        setTimeout(() => {
          // Close the signup modal
          closeSignup();
          
          // Show the success modal
          setIsSuccessModalOpen(true);
        }, 3000); // Show the success message in the signup modal for 3 seconds before transitioning
      } else {
        console.error('Signup failed:', response.error || 'Unknown error');
        // Update the form data with the error
        setSignupFormData(prevData => ({
          ...prevData,
          submitResult: {
            success: false,
            message: response.error || 'Unknown error'
          }
        }));
      }
    } catch (error) {
      console.error('Error submitting signup form:', error);
      // Update the form data with the error
      setSignupFormData(prevData => ({
        ...prevData,
        submitResult: {
          success: false,
          message: error.message || 'Unknown error'
        }
      }));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Function to close the success modal
  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
  };
  
  // Expose the closePackageComparisonModal function to the window object
  // This allows other components to access it
  useEffect(() => {
    // Create a ref to the component
    const componentRef = {
      __closePackageComparisonModal: closePackageComparisonModal
    };
    
    // Add a data attribute to the component
    const wonderNavElement = document.createElement('div');
    wonderNavElement.setAttribute('data-wonder-nav', 'true');
    
    // Attach the ref to the element
    Object.assign(wonderNavElement, componentRef);
    
    // Add the element to the DOM
    document.body.appendChild(wonderNavElement);
    
    // Clean up when the component unmounts
    return () => {
      document.body.removeChild(wonderNavElement);
    };
  }, []);
  
  // Listen for the open-coverage-sidebar event
  useEffect(() => {
    const handleOpenCoverageSidebar = (event) => {
      console.log('Received open-coverage-sidebar event:', event.detail);
      // Open the coverage sidebar
      setIsCoverageSidebarOpen(true);
    };
    
    // Add event listener
    window.addEventListener('open-coverage-sidebar', handleOpenCoverageSidebar);
    
    // Clean up
    return () => {
      window.removeEventListener('open-coverage-sidebar', handleOpenCoverageSidebar);
    };
  }, []);
  
  return (
    <>
      {/* QuickNav Bar - appears when scrolling down */}
      <QuickNavBar
        fixed={true}
        isVisible={showQuickLinks && !isCoverageSidebarOpen && !isComparisonModalOpen && !isSignupOpen}
        openCoverageSidebar={openCoverageSidebar}
        openComparisonModal={openPackageComparisonModal}
      />
      
      {/* Main Navigation Components */}
      <NavigationBar
        openCoverageSidebar={openCoverageSidebar}
        mobileMenuAction={mobileMenuAction}
        setMobileMenuAction={setMobileMenuAction}
        isUsingGeolocation={isUsingGeolocation}
        setIsUsingGeolocation={setIsUsingGeolocation}
        isHidden={showQuickLinks && !isCoverageSidebarOpen && !isComparisonModalOpen && !isSignupOpen}
        openComparisonModal={openPackageComparisonModal}
        openRecommendationModal={openRecommendationModal}
        openRouterFeaturesModal={openRouterFeaturesModal}
        openISPFeaturesModal={openISPFeaturesModal}
      />
      {/* MobileBottomNav is already included in MobileNavComponent */}
      
      {/* Global Geolocation Warning - positioned at the bottom of the page with z-index higher than the compare modal (z-40) */}
      {isUsingGeolocation && (
        <div className="fixed bottom-0 inset-x-0 z-[60] bg-amber-600 text-white py-2 px-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center flex-1 mr-4">
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span className="font-medium">Using approximate location. For accurate coverage results, please enter your exact address including unit number.</span>
          </div>
          <button
            className="px-3 py-1 bg-white text-amber-700 rounded-full text-sm font-medium hover:bg-amber-100 transition-colors flex-shrink-0"
            onClick={() => {
              setIsUsingGeolocation(false);
              if (isCoverageSidebarOpen) {
                closeCoverageSidebar();
                setTimeout(() => {
                  setIsCoverageSidebarOpen(true);
                }, 100);
              }
            }}
          >
            Enter address
          </button>
        </div>
      )}
      
      {/* Coverage Sidebar - Adjust width when in side-by-side layout */}
      <CoverageSidebar
        isOpen={isCoverageSidebarOpen}
        onClose={closeCoverageSidebar}
        initialCoordinates={initialCoordinates}
        initialAddress={initialAddress}
        openPackageComparisonModal={openPackageComparisonModal}
        onOpenRecommendations={openRecommendationModal}
        isSideBySideLayout={isSideBySideLayout}
        // Pass filter state and setters
        selectedOperators={selectedOperators}
        setSelectedOperators={setSelectedOperators}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        speedFilter={speedFilter}
        setSpeedFilter={setSpeedFilter}
        priceFilter={priceFilter}
        setPriceFilter={setPriceFilter}
        // Pass geolocation state
        isUsingGeolocation={isUsingGeolocation}
        setIsUsingGeolocation={setIsUsingGeolocation}
        // Pass lifted address state
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        // selectedPlaceCoordinates={selectedPlaceCoordinates} // Remove prop
        // setSelectedPlaceCoordinates={setSelectedPlaceCoordinates} // Remove setter prop as state is removed
      />
      
      {/* Package Comparison Modal - Rendered at the top level */}
      <PackageComparisonModal
        isOpen={isComparisonModalOpen}
        onClose={closePackageComparisonModal}
        selectedOperator={selectedOperator}
        allOperators={allOperators}
        isSideBySideLayout={isSideBySideLayout}
        // Pass filter state
        selectedOperators={selectedOperators}
        sortBy={sortBy}
        sortDirection={sortDirection}
        speedFilter={speedFilter}
        priceFilter={priceFilter}
        // Pass signup function, geolocation state, and coordinates
        openSignup={openSignup}
        isUsingGeolocation={isUsingGeolocation}
        // selectedPlaceCoordinates={selectedPlaceCoordinates} // Remove prop
      />
      
      {/* Signup Components */}
      {isSignupOpen && (
        <>
          <SignupSummary
            packageData={signupPackage}
            formData={signupFormData}
            installationAddress={selectedAddress}
            isSideBySideLayout={true}
          />
          <SignupModal
            isOpen={isSignupOpen}
            onClose={closeSignup}
            packageData={signupPackage}
            installationAddress={selectedAddress} // Still pass the address object
            providerName={signupProviderName}
            // coordinates={signupCoordinates} // Remove prop
            isSideBySideLayout={true}
            formData={signupFormData}
            updateFormData={updateSignupFormData}
            onSubmit={handleSignupSubmit}
            isSubmitting={isSubmitting}
          />
        </>
      )}
      
      {/* Success Modal - shown after successful signup */}
      <SignupSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={closeSuccessModal}
        referenceNumber={referenceNumber}
      />
      
      {/* ComparisonModalManager - handles both comparison and recommendation */}
      <ComparisonModalManager
        isOpen={isComparisonModalOpen || isRecommendationModalOpen} // Only open when needed
        onClose={() => {
          setIsComparisonModalOpen(false);
          setIsRecommendationModalOpen(false);
          closeAllModals();
        }}
        onSelectPackage={(pkg) => {
          // If external function is provided, use it
          if (typeof openSignup === 'function') {
            openSignup(pkg, pkg.operatorName);
          }
        }}
        openCoverageSidebar={() => {
          setIsCoverageSidebarOpen(true);
          setIsComparisonModalOpen(false);
          setIsRecommendationModalOpen(false);
          closeAllModals();
        }}
      />
    </>
  );
});

// Export all components for individual use if needed
export {
  WonderNavigation as default,
  NavigationBar,
  MobileBottomNav,
  CoverageSidebar,
  AddressSearchResults,
  CoverageResults,
  MegaMenuManager,
  PackageComparisonModal,
  SignupModal,
  SignupSummary
};

// Also export the individual navigation-bar components for direct use if needed
export * from './navigation-bar';

// Export the mega-menu components
export * from './mega-menus';